import { useEffect } from 'react';
import { useTranslation } from '@doccareplus/shared/lib';
import { GlobalSearchPanel } from '../shared/GlobalSearchPanel';
import { useGlobalSearchController, type UICommand } from '../shared/useGlobalSearchController';

export interface CommandPaletteProps {
  open: boolean;
  onClose: () => void;
  /** Static navigation pages + quick actions, supplied (and capability-filtered) by the shell. */
  commands: UICommand[];
}

/**
 * CommandPalette — the desktop global-search surface: a centered overlay opened from the header
 * search box or ⌘K / Ctrl-K. Wraps the shared {@link GlobalSearchPanel} in a modal with a
 * backdrop and keyboard legend. Search behaviour lives entirely in the shared panel/controller.
 */
export function CommandPalette({ open, onClose, commands }: CommandPaletteProps) {
  const { t } = useTranslation('common');
  const controller = useGlobalSearchController(commands, onClose);
  const { setTerm } = controller;

  // Start each session from a clean box.
  useEffect(() => {
    if (open) setTerm('');
  }, [open, setTerm]);

  // Lock background scroll while the palette is open.
  useEffect(() => {
    if (!open) return;
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previous;
    };
  }, [open]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[60] animate-in fade-in duration-150">
      <button
        type="button"
        aria-label={t('search.close')}
        onClick={onClose}
        className="absolute inset-0 h-full w-full cursor-default bg-[rgba(11,16,14,.45)] backdrop-blur-sm"
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-label={t('search.title')}
        onKeyDown={(event) => {
          if (event.key === 'Escape') onClose();
        }}
        className="relative mx-auto mt-[12vh] flex max-h-[70vh] w-[92vw] max-w-[600px] flex-col overflow-hidden rounded-[16px] border border-[var(--border)] bg-[var(--surface)] shadow-[var(--shadow-3)] animate-in slide-in-from-top-2 duration-150"
      >
        <GlobalSearchPanel controller={controller} showKeyboardHints />
      </div>
    </div>
  );
}
